import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { DayPicker, type Modifiers } from 'react-day-picker'
import { ru } from 'react-day-picker/locale'
import 'react-day-picker/style.css'
import {
  getAppointmentAvailableTime,
  getAppointmentComment,
  getAppointmentDate,
  getAppointmentSalon,
  getAppointmentServicesFull,
  getAppointmentTime,
  getIsAvailableTimeLoading,
  removeAppointmentService,
  setAppointmentComment,
  setAppointmentDate,
  setAppointmentServices,
  setAppointmentTime,
  unsetAppointmentServices,
} from 'src/store/appointment.store'
import { setSelectedCityId } from 'src/store/app.state.store'
import { getCities, getSelectedCity } from 'src/store/data.store'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { Arrow } from 'components/Utility/Arrow/Arrow'
import { Popup } from 'components/Utility/Popup/Popup'
import { Loader } from 'components/Utility/Loader/Loader'
import { CityPicker } from 'components/PageElements/CityPicker/CityPicker'
import { ClientSummary } from '../ClientsSummary/ClientsSummary'
import { useClientsList } from '../useClientsList'
import { Step } from './Step'
import { Time } from './Time'
import styles from './AppointmentForm.module.scss'

const MAX_COMMENT_LENGTH = 140
const BOOKING_MONTHS_AHEAD = 2
const BROWS_DIVISION = 2

const dayPickerModifiers = {
  weekend: { dayOfWeek: [0, 6] },
}

const dayPickerModifiersClassNames = {
  weekend: 'rdp-weekend',
}

type AppointmentFormProps = {
  onSubmit: () => void,
  onChooseSalon: () => void,
  onChooseServices: (clientIndex: number) => void,
}

/** The booking form: city, salon, services per guest, date, time and comment */
export const AppointmentForm: React.FC<AppointmentFormProps> = React.memo((props) => {
  const { onSubmit, onChooseSalon, onChooseServices } = props
  const [isDatePickerActive, setDatePickerActive] = useState(false)
  const [isCityPickerActive, setCityPickerActive] = useState(false)

  const dispatch = useDispatch()
  const selectedCity = useSelector(getSelectedCity)
  const salon = useSelector(getAppointmentSalon)
  const clientsIndex = useSelector(getAppointmentServicesFull)
  const clients = useClientsList(clientsIndex)
  const date = useSelector(getAppointmentDate)
  const availableTime = useSelector(getAppointmentAvailableTime)
  const availableTimeLoading = useSelector(getIsAvailableTimeLoading)
  const time = useSelector(getAppointmentTime)
  const cities = useSelector(getCities)
  const comment = useSelector(getAppointmentComment)

  const showCityPicker = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setCityPickerActive(true)
  }, [])

  const showDayPicker = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setDatePickerActive(true)
  }, [])

  const handleSelectCity = useCallback((cityId: number, event: React.MouseEvent) => {
    event.preventDefault()
    dispatch(setSelectedCityId(cityId))
  }, [dispatch])

  const handleDayClick = useCallback((day: Date, { disabled }: Modifiers) => {
    if (disabled) {
      return
    }
    dispatch(setAppointmentDate(moment(day).format('YYYY-MM-DD')))
    setDatePickerActive(false)
  }, [dispatch])

  const freeTime = useMemo(() => availableTime?.filter((slot) => slot.isFree) ?? null, [availableTime])

  const selectedDate = date ? new Date(date) : new Date()
  const upperDateLimit = moment().add(BOOKING_MONTHS_AHEAD, 'months').toDate()
  const commentTooLong = comment.length > MAX_COMMENT_LENGTH
  const hasServices = clients.some((services) => services.length > 0)
  const submitDisabled = !salon || !hasServices || !date || !time || commentTooLong

  const renderTime = () => {
    if (freeTime === null || salon === null) {
      return <div className={styles.timeEmpty}>Выберите салон, услуги и дату, чтобы увидеть доступное время</div>
    }
    const hasBrows = clients.some((services) => services.some((service) => service.division === BROWS_DIVISION))
    if (hasBrows && !salon.divisions.includes(BROWS_DIVISION)) {
      return <div className={styles.timeEmpty}>В выбранном салоне недоступны услуги бровиста</div>
    }
    if (!freeTime.length) {
      return <div className={styles.timeEmpty}>Нет доступного времени на выбранную дату</div>
    }
    return <Time time={freeTime} onSelect={(value) => dispatch(setAppointmentTime(value))} selected={time} />
  }

  const salonVars = salon?.meta?.templateVars?.pageVars

  return (
    <div className={styles.container}>
      <div className={styles.steps}>
        <Step name={'city'} title={'Выберите город'}>
          <div className={styles.cityPicker}>
            <div className={styles.appointmentCityPicker}>
              <div className={styles.cityName} onClick={showCityPicker}>
                {selectedCity ? selectedCity.city.name : ''}
              </div>
              <Popup
                backDropClassName={styles.popupBackDrop}
                className={styles.popup}
                active={isCityPickerActive}
                onClose={() => setCityPickerActive(false)}
              >
                <CityPicker
                  cities={cities}
                  selectedCityId={selectedCity?.city.id ?? null}
                  onSelectCity={handleSelectCity}
                />
              </Popup>
            </div>
            <div className={styles.pickerContainer}>
              <Arrow direction={'right'} />
            </div>
          </div>
        </Step>
        <Step name={'salon'} title={'Выберите салон'}>
          <div className={styles.salonPicker} onClick={onChooseSalon}>
            <div className={styles.salonName}>
              {salon ? (
                <span>
                  <span className={styles.salonTitle}>
                    {salonVars?.salonName}
                  </span>
                  <span> | </span>
                  <span className={styles.salonAddress}>
                    {salonVars?.salonAddress}
                  </span>
                </span>
              ) : 'Не выбран'}
            </div>
            <div className={styles.pickerContainer}>
              <Arrow direction={'right'} />
            </div>
          </div>
        </Step>
        <Step name={'service'} title={'Выберите услуги'}>
          <div className={styles.clients}>
            {clients.map((services, index) => (
              <div key={index} className={styles.client}>
                <div className={styles.summary}>
                  {clients.length > 1 &&
                    <div className={styles.clientHeader}>
                      <span>Гость №{index + 1}</span>
                    </div>}
                  {services.length > 0 &&
                    <ClientSummary
                      salon={salon}
                      index={index}
                      totalClients={1}
                      editable
                      services={services}
                      onItemDelete={(serviceId) => {
                        dispatch(removeAppointmentService({ clientIndex: index, serviceId }))
                      }}
                    />}
                </div>
                <div className={styles.clientActions}>
                  <div className={styles.action} data-action={'add'} onClick={() => onChooseServices(index)}>
                    <i className={styles.actionIcon} />
                    <div className={styles.actionTitle}>Добавить услугу</div>
                  </div>
                  {clients.length === 1 ? (
                    <div
                      className={styles.action}
                      data-action={'add'}
                      onClick={() => dispatch(setAppointmentServices({ clientIndex: index + 1, services: [] }))}
                    >
                      <i className={styles.actionIcon} />
                      <div className={styles.actionTitle}>Добавить гостя</div>
                    </div>
                  ) : (
                    <div
                      className={styles.action}
                      data-action={'delete'}
                      onClick={() => dispatch(unsetAppointmentServices({ clientIndex: index }))}
                    >
                      <i className={styles.actionIcon} />
                      <div className={styles.actionTitle}>Удалить гостя</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Step>
        <Step name={'date'} title={'Выберите дату'}>
          <div className={styles.datePicker} onClick={showDayPicker}>
            <span className={styles.date}>{moment(selectedDate).format('DD.MM.YYYY')}</span>
            <div className={styles.pickerContainer}>
              <Arrow direction={isDatePickerActive ? 'down' : 'right'} />
              <Popup active={isDatePickerActive} onClose={() => setDatePickerActive(false)}>
                <div className={styles.dayPicker}>
                  <DayPicker
                    mode={'single'}
                    locale={ru}
                    navLayout={'around'}
                    defaultMonth={selectedDate}
                    selected={selectedDate}
                    modifiers={dayPickerModifiers}
                    modifiersClassNames={dayPickerModifiersClassNames}
                    onDayClick={handleDayClick}
                    disabled={[{ before: new Date() }, { after: upperDateLimit }]}
                  />
                </div>
              </Popup>
            </div>
          </div>
        </Step>
        <Step name={'time'} title={'Выберите время'}>
          <div className={styles.stepContent}>
            <div className={styles.timeContainer}>
              {availableTimeLoading ? <Loader /> : renderTime()}
            </div>
          </div>
        </Step>
        <Step name={'comment'} title={'Оставьте комментарий'}>
          <div className={styles.stepContent}>
            <div className={clsx([styles.commentContainer, { [styles.error]: commentTooLong }])}>
              <textarea
                className={styles.comment}
                value={comment}
                onChange={(e) => dispatch(setAppointmentComment(e.target.value))}
              />
              <div className={styles.commentLength}>{comment.length} / {MAX_COMMENT_LENGTH}</div>
            </div>
          </div>
        </Step>
      </div>
      <div className={styles.actions}>
        <AppButton disabled={submitDisabled} onClick={onSubmit}>Продолжить</AppButton>
      </div>
    </div>
  )
})
AppointmentForm.displayName = 'AppointmentForm'
